import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightning, ArrowRight, ArrowsClockwise, Building, EnvelopeSimple, Lock, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

export default function SignupPage() {
  const navigate = useNavigate();
  const { registerCompany } = useAuth();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setSubmitting(true);
    try {
      await registerCompany(form.email.trim(), form.password, form.name.trim() || undefined, form.company_name.trim());
      toast.success(`Welcome to Load Logic, ${form.company_name}!`);
      // Mark this user as fresh-signup so the demo tour fires on first dashboard load
      try { localStorage.setItem("ll_first_run", "1"); } catch (_e) {}
      navigate("/live", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) toast.error(detail[0]?.msg || "Sign-up failed");
      else toast.error(detail || "Sign-up failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#ff8c00]">
            <Lightning size={28} weight="fill" />
            <span className="font-heading font-black text-2xl tracking-tight">LOAD LOGIC</span>
          </Link>
        </div>

        <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
          <div className="border-b border-[#2a2a2a] px-6 py-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1">14-day free trial · no card</div>
            <h1 className="font-heading font-black text-xl tracking-tight">Spin up your dispatch yard</h1>
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <Field label="Company name" icon={Building}>
              <input
                required
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Cascade Haulers Inc"
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="signup-company"
              />
            </Field>
            <Field label="Your name (optional)" icon={User}>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Dispatcher"
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="signup-name"
              />
            </Field>
            <Field label="Work email" icon={EnvelopeSimple}>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@yourcompany.com"
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="signup-email"
              />
            </Field>
            <Field label="Set password" icon={Lock}>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 8 characters"
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="signup-password"
              />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-xs uppercase tracking-[0.25em] font-black flex items-center justify-center gap-2"
              data-testid="signup-submit"
            >
              {submitting ? <ArrowsClockwise size={14} className="animate-spin" /> : <ArrowRight size={14} weight="bold" />}
              Create my dispatch yard
            </button>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] text-center pt-2">
              By creating an account you agree to fair-use terms.
            </div>
          </form>
        </div>

        <div className="text-center mt-4">
          <Link to="/login" className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] hover:text-[#ff8c00]">
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1 flex items-center gap-1">
        {Icon && <Icon size={10} weight="bold" />} {label}
      </label>
      {children}
    </div>
  );
}
